/**
 * Where a ride came from, as the API reports it in ``provider``
 * (docs/RIDE_SOURCES_UI_PLAN.md §3): an icon and a label each. Brand names
 * stay untranslated; ``file`` and ``other`` have their own copy.
 */
import type { LucideName } from '../components/icons/LucideIcon.tsx';

export const PROVIDERS = [
  'file', 'apple_health', 'health_connect', 'garmin', 'intervals', 'strava', 'other',
] as const;
export type Provider = (typeof PROVIDERS)[number];

export const PROVIDER_ICON: Record<Provider, LucideName> = {
  file: 'file-up',
  apple_health: 'heart-pulse',
  health_connect: 'heart-pulse',
  garmin: 'watch',
  intervals: 'activity',
  strava: 'refresh-cw',
  other: 'circle-dot',
};

const BRAND_NAMES: Partial<Record<Provider, string>> = {
  apple_health: 'Apple Health',
  health_connect: 'Health Connect',
  garmin: 'Garmin',
  intervals: 'intervals.icu',
  strava: 'Strava',
};

export function isProvider(value: unknown): value is Provider {
  return PROVIDERS.includes(value as Provider);
}

/** ``provider`` from the API, ``other`` when missing or unknown. */
export function toProvider(value: unknown): Provider {
  return isProvider(value) ? value : 'other';
}

/** The provider's name: a brand as is, else the translated word. */
export function providerLabel(provider: Provider, t: (key: string) => string): string {
  return BRAND_NAMES[provider] ?? t(`sources.provider.${provider}`);
}
